<template>
  <v-card flat data-id-e2e="clusteringPage" class="step-tab-panel">
    <ScenarioMeasuringHeader
      :header-text="tkey('reviewClusteringMessage')"
      :columns="4"
      tooltip-link="toolguide/100-clustering.html"
    />

    <v-container
      fluid
      class="pa-0 flex-grow-1 d-flex flex-column"
      :class="{ 'in-progress': hasJobInProgress, disabled: isRunningClusteredSwitchingJob }"
    >
      <v-row class="flex-grow-0 ma-0">
        <v-col
          cols="2"
          class="rtls-border rtls-border--right created-cluster-schemes d-flex flex-column justify-end"
        >
          <span class="pa-3 font-weight-bold d-block">
            {{ tkey('clusteringSchemes') }}
          </span>
          <div>
            <!-- Headers -->
            <v-row id="headers-row">
              <v-col
                v-for="(header, index) in headers"
                :key="`header-${index}`"
                :cols="header.cols"
                :class="{
                  disabled: isNavigationDisabled,
                  ...header.headerClass,
                }"
                class="header-text"
              >
                <span>{{ header.text }}</span>
              </v-col>
            </v-row>
          </div>
        </v-col>

        <v-col cols="10" style="padding: 0">
          <v-tabs
            :model-value="currentTab"
            hide-slider
            height="35"
            @update:model-value="onTabChange"
          >
            <v-tab
              v-for="(tab, tabIndex) in tabs"
              :key="tab.routeName"
              :class="{
                'step-tab': true,
                'first-tab': tabIndex === 0,
              }"
              :disabled="isNavigationDisabled || tab.disabled || !selectedScheme"
              :to="generateTabRoute(tab)"
              :value="tab.position"
              @click="handleTabSelect(tab)"
            >
              <div class="d-flex align-center">
                <v-icon v-if="tab.icon" :icon="tab.icon" size="small" class="mr-2" />
                {{ tab.title }}
              </div>
            </v-tab>
          </v-tabs>

          <div v-if="userCanSeeSchemeInformation" class="d-flex">
            <v-row class="header ma-0">
              <!-- Title -->
              <div class="d-flex align-center ml-3">
                <span class="font-weight-bold mr-1">{{ getInfoTextForTab }}</span>
              </div>

              <!-- Attribute dropdown on the deviation tab -->
              <div v-if="showAttributeFilter" class="d-flex align-center ml-5">
                <span class="mr-2">{{ t('general.attribute') }}</span>
                <v-select
                  :model-value="selectedAttributeId"
                  flat
                  :items="getClusterSchemeAttributes"
                  :placeholder="tkey('attributeFilterPlaceholder')"
                  item-title="name"
                  item-value="id"
                  :disabled="hasJobInProgress"
                  width="240px"
                  density="compact"
                  hide-details
                  @update:model-value="changeSelectedAttribute"
                />
              </div>

              <!-- Compare with drop down on the comparison tab  -->
              <div v-if="showCompareWithFilter" class="d-flex align-center ml-5">
                <span style="white-space: nowrap" class="mr-2">
                  {{ t('general.compareWith') }}
                </span>
                <v-select
                  :items="comparisonSchemas"
                  :placeholder="t('general.select')"
                  :model-value="comparisonSchemeId"
                  item-title="name"
                  item-value="_id"
                  width="240px"
                  density="compact"
                  hide-details
                  @update:model-value="setSelectedComparisonScheme"
                />
              </div>

              <!-- Toggle for list / map view when on store allocation tab -->
              <div v-if="showListModeToggle" class="d-flex align-center ml-2">
                <!--
                  can't use v-btn-toggle because it immediately updates internal lazy value.
                  need to wait for unsaved data warning modal confirmation if hasDataChanges.
                  https://github.com/vuetifyjs/vuetify/issues/10622#issuecomment-656930871
                -->
                <v-item-group class="v-btn-toggle">
                  <v-btn
                    :class="{ 'v-btn--active': listMapToggle === 0 }"
                    @click="listMapToggle = 0"
                  >
                    {{ tkey('list') }}
                  </v-btn>
                  <v-btn
                    :class="{ 'v-btn--active': listMapToggle === 1 }"
                    @click="listMapToggle = 1"
                  >
                    {{ tkey('map') }}
                  </v-btn>
                </v-item-group>
                <v-select
                  v-show="listMapToggle === 0"
                  id="vselectClusterName"
                  v-model="selectedClusterToMoveTo"
                  :disabled="!hasSelectedStores"
                  :items="selectableClusterNames"
                  :placeholder="tkey('moveSelectedStoresToCluster')"
                  hide-details
                  flat
                  density="compact"
                  class="ml-4 mt-2"
                  @update:model-value="moveStores($event)"
                />
              </div>
            </v-row>
          </div>
        </v-col>
      </v-row>

      <v-divider horizontal />

      <v-row
        id="main-row"
        class="flex-grow-1 ma-0"
        :class="isRunningClusteredSwitchingJob && 'disabled'"
      >
        <v-col cols="2" class="rtls-border rtls-border--right created-cluster-schemes">
          <v-container
            id="cluster-schemes-table"
            :class="{ 'empty-table': !getScenarioClusterSchemes.length }"
          >
            <!-- Body -->
            <div v-for="group in groupedSchemes" :key="group.text">
              <v-row v-if="group.text">
                <span class="font-weight-bold pl-2">{{ group.text }}</span>
              </v-row>
              <v-row
                v-for="row in group.schemes"
                :key="row._id"
                class="cluster-row d-flex"
                :class="{
                  'in-scope': group.inScope,
                  selected: selectedScheme && selectedScheme._id === row._id,
                }"
              >
                <v-col
                  v-for="header in headers"
                  :key="`cell-${row._id}-${header.id}`"
                  style="height: 30px"
                  :cols="header.cols"
                  :class="['d-flex align-center', header.colClass]"
                >
                  <template v-if="header.ui === CELL_TYPE.icon">
                    <v-icon
                      class="v-icon-blue"
                      :disabled="isNavigationDisabled || row.disabled"
                      size="30"
                      @click="header.onClick(row)"
                    >
                      {{ header.value }}
                    </v-icon>
                  </template>
                  <template v-else>
                    {{ getClusterVariance(header.type, row[header.value]) }}
                    <span
                      v-if="showClusterWarning(header.value, row)"
                      class="cluster-warning-container ml-2"
                      @click="header.onClick(row, storeAllocationTab)"
                    >
                      <i class="material-icons-round error-triangle-icon">warning_amber</i>
                    </span>
                  </template>
                </v-col>
              </v-row>
            </div>
          </v-container>

          <v-divider horizontal />

          <v-btn
            primary
            class="mt-2 ml-2"
            :disabled="hasJobInProgress || isEditingDisabled"
            @click="openClusterGenerationModal"
          >
            {{ tkey('addClusteringSchemes') }}
          </v-btn>

          <div
            v-show="invalidClusteringSchemes.length"
            id="cluster-warning-section"
            class="mt-2 ml-2"
          >
            <v-divider horizontal />
            <div class="cluster-warning-container mt-2 mr-1">
              <i class="material-icons-round error-triangle-icon">warning_amber</i>
              <span>
                <b class="ml-2 mr-1">{{ tkey('clusterWarningInfoTitle') }}</b>
                {{ tkey('clusterWarningInfoMessage') }}
              </span>
            </div>
          </div>
        </v-col>

        <v-col
          id="clustering-tab-content"
          cols="10"
          :class="{ 'pt-5': !isStoreAllocationMapView, 'pa-0': isStoreAllocationMapView }"
          class="pb-0 pl-0 pr-0"
        >
          <component
            :is="componentName"
            v-if="userCanSeeSchemeInformation"
            :ref="componentName"
            @data-changed="hasDataChanges = $event"
            @has-selected-rows="hasSelectedStores = $event"
          />
        </v-col>
      </v-row>

      <ClusterGenerationModal
        v-model="isClusterGenerationModalOpen"
        @process="processNewClusterSchemes"
        @close="closeClusterGenerationModal"
      />

      <ClusterSwitchingModal
        v-if="getScenarioClusterSchemes.length"
        v-model="isClusteredSwitchingModalOpen"
        @process="processClusteredSwitching"
        @close="closeClusteredSwitchingModal"
      />
    </v-container>

    <div class="page-actions-container">
      <PageActions
        :show-export="!isStoreAllocationMapView"
        include-scenario-in-export-name
        save-btn-text="runClusteredSwitching"
        export-service="clustering"
        :save-disabled="isEditingDisabled"
        :is-discard-enabled="!isEditingDisabled"
        :has-data-changes="userCanRunClusteredSwitching"
        :has-data-errors="hasDataErrors"
        :show-discard="false"
        :live-data="getScenarioClusterSchemesForExport"
        @save="openClusterSwitchingModal"
      >
        <template #right-btns>
          <v-btn
            class="text-outline"
            secondary
            variant="flat"
            :disabled="selectedScenario.hasSkippedClustering === false || isEditingDisabled"
            @click="setScenarioHasSkippedClustering({ hasSkippedClustering: true })"
          >
            {{ t('actions.skipClustering') }}
          </v-btn>
        </template>
      </PageActions>
    </div>

    <div class="center-top">
      <ProgressBar
        v-if="isLoadingClusterInformation || hasJobInProgress"
        :message="t('messages.inProgress')"
        :percentage="getProgressPercent"
        :status="getProgressStatus"
      />
      <div
        v-if="isRunningClusteredSwitchingJob && switchingClusters.length"
        class="switching-cluster-status"
      >
        <p class="switching-cluster-status__scheme-name">
          {{ selectedScheme?.name }}
        </p>
        <div
          v-for="cluster in switchingClusters"
          :key="cluster.clusterId"
          class="switching-cluster-status__row"
        >
          <v-icon :color="clusterStatusIconColor(cluster.status)" size="small">
            {{ clusterStatusIcon(cluster.status) }}
          </v-icon>
          <span class="switching-cluster-status__cluster-name">{{ cluster.clusterName }}</span>
          <span class="switching-cluster-status__cann-group-count">
            ({{ completedCannGroupCount(cluster) }}/{{ cluster.cannGroups?.length ?? 0 }})
          </span>
        </div>
      </div>
    </div>

    <UnsavedDataModal
      ref="unsavedDataModal"
      :value="isUnsavedDataModalOpen"
      @cancel="closeUnsavedDataModal"
      @confirm="confirmUnsavedDataModal"
    />
  </v-card>
</template>

<script>
import { getCurrentInstance, nextTick } from 'vue';
import {
  camelCase,
  filter,
  get,
  groupBy,
  has,
  includes,
  keyBy,
  map,
  partition,
  some,
  sumBy,
} from 'lodash';
import { mapActions, mapState } from 'pinia';

// enums
import TableEnum from '@enums/table/index';
import switchingJobStatuses from '@enums/switching-job-statuses';

// composables
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useFormatNumber } from '@/composables/use-format-number';
import { useFeature } from '@/composables/use-feature';
import { useScenarioStatusFormatter } from '@/composables/use-scenario-status-formatter';
import { useAiAgentActions } from '@/composables/use-ai-agent-actions';
import { useClusteredSwitchingStatus } from '@/composables/use-clustered-switching-status';
import { useAiStore } from '@/stores/ai';

// stores
import { useClusteringStore } from '@/stores/clustering';
import { useContextStore } from '@/stores/context';
import { useReferenceClusteringStore } from '@/stores/reference-clustering';
import { useScenariosStore } from '@/stores/scenarios';
import { useSnackbarStore } from '@/stores/snackbar';
import { useSwitchingMatricesStore } from '@/stores/switching-matrices';
import { useToolDataStore } from '@/stores/tool-data';

// components

// ─────────────────────────────────────────────────────────────────────────────
// AI analysis guidance constants
// ─────────────────────────────────────────────────────────────────────────────

const DEVIATIONS_GUIDANCE = `
INTERPRETATION GUIDE — read before analysing the data below.
- Deviations are in PERCENTAGE POINTS (pp) from the unclustered baseline — not relative %.
  e.g. +3.5 pp means this cluster has 3.5 percentage points MORE of this attribute than the overall average.
- Magnitude: ≥3 pp = strong signal; 1–3 pp = meaningful; <1 pp = usually noise, skip unless it forms a pattern.
- Clusters with isNoDataCluster: true have no transaction data — note them separately, do not compare.
- Source data may use comma-decimals (e.g. "12,5") — parse as 12.5.
- Focus on the largest absolute deviations first. Compare clusters to each other to surface contrasts.
- Do not infer causality. Do not invent attributes not present in the data.
- Do not add conclusions about the value of clustering itself (e.g. "this shows your clustering is working well" or "these insights can inform your merchandising strategy") — stick to what the numbers show.
- If signal is weak (all deviations <1 pp), say so briefly rather than padding with low-value observations.

DATA:
`.trimStart();

export default {
  components: {},

  beforeRouteUpdate(to, from, next) {
    this.updateSelectedAttributeId();
    this.beforeRouteLeave(to, from, next);
  },

  setup() {
    const { canEditClusteringPage } = usePermission();
    const { aiPanelEnabled, aiClusteringAnalysisEnabled, productSwitchingServiceEnabled } =
      useFeature();

    const {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      beforeNavWithUnsavedData,
    } = useUnsavedDataWarning();

    const { tkey, t } = useInternationalization('clusteringPage');

    const { formatNumber } = useFormatNumber();

    const { formatScenarioProgress } = useScenarioStatusFormatter('clustering');

    const instance = getCurrentInstance();
    const clusteringStore = useClusteringStore();
    const scenariosStore = useScenariosStore();
    const {
      start: startClusteredSwitchingPoller,
      clusters: switchingClusters,
      serviceJobProgress: clusteredSwitchingProgress,
    } = useClusteredSwitchingStatus({
      getScenarioId: () => instance.proxy.$route.params.scenarioId,
      onTerminal: async () => {
        await scenariosStore.loadScenario(scenariosStore.selectedScenario._id);
      },
    });

    useAiAgentActions({
      allowScenarioWorkflowNavigation: true,
      getExclude: () => {
        const schemes = clusteringStore.getScenarioClusterSchemes;
        if (!schemes.length) {
          return [
            'select-cluster-scheme',
            'navigate-to-tab',
            'change-attribute-filter',
            'run-clustered-switching',
            'analyse-clustering-deviations',
          ];
        }
        const excluded = [];
        const selected = clusteringStore.selectedScheme;
        if (instance.proxy.isEditingDisabled || instance.proxy.hasJobInProgress || !selected) {
          excluded.push('run-clustered-switching');
        }
        if (!selected) {
          excluded.push('analyse-clustering-deviations');
        }
        if (!selected || instance.proxy.selectedTab?.componentName !== 'deviation') {
          excluded.push('change-attribute-filter');
        }
        return excluded;
      },
      getSupplementary: () => {
        const supp = {};
        const schemes = clusteringStore.getScenarioClusterSchemes;
        if (schemes.length) {
          supp['select-cluster-scheme'] =
            `Available schemes: ${schemes.map(s => s.name).join(', ')}.`;
        }
        supp['navigate-to-tab'] =
          `Current tab: ${instance.proxy.selectedTab?.componentName || 'comparison'}.`;
        const attrs = clusteringStore.getClusterSchemeAttributes;
        if (attrs.length) {
          const attrList = attrs.map(a => `${a.name} (id: ${a.id})`).join(', ');
          supp['change-attribute-filter'] = `Available attributes: ${attrList}.`;
          supp['navigate-to-tab'] += ` Available attributes for deviation tab: ${attrList}.`;
        }
        return supp;
      },
      actions: {
        'select-cluster-scheme': {
          description:
            'Select a clustering scheme by name, loading its deviation and store allocation data into the main view.',
          workingLabel: 'Selecting cluster scheme',
          readOnly: true,
          parameters: {
            type: 'object',
            properties: {
              name: {
                type: 'string',
                description: 'The exact name of the clustering scheme to select.',
              },
            },
            required: ['name'],
          },
          handler: async ({ name }) => {
            const schemes = clusteringStore.getScenarioClusterSchemes;
            const scheme = schemes.find(s => s.name === name);
            if (!scheme) {
              const available = schemes.map(s => s.name).join(', ');
              return `[Tool] Action failed: scheme "${name}" not found. Available schemes: ${available || 'none'}. Ask the user to clarify the name.`;
            }
            if (scheme.disabled) {
              return `[Tool] Action failed: scheme "${name}" is disabled and cannot be selected.`;
            }
            instance.proxy.expandClusteringScheme(scheme);
            await nextTick();
            const selected = clusteringStore.selectedScheme;
            if (!selected || selected._id !== scheme._id) {
              return `[Tool] Action failed: scheme "${name}" could not be selected. Ask the user to try again.`;
            }
            return `[Tool] Action succeeded: selected cluster scheme "${name}" (${scheme.clusters?.length || 0} clusters).`;
          },
        },
        'navigate-to-tab': {
          description:
            'Navigate to one of the three clustering sub-tabs: comparison (compare scheme distributions), deviation (attribute deviations per cluster), or store-allocation (view and edit store assignments). When navigating to the deviation tab after an analysis, pass attributeId to set the attribute filter in the same call — do not call change-attribute-filter separately.',
          workingLabel: 'Navigating to tab',
          readOnly: true,
          parameters: {
            type: 'object',
            properties: {
              tab: {
                type: 'string',
                enum: ['comparison', 'deviation', 'store-allocation'],
                description: 'The tab to navigate to.',
              },
              attributeId: {
                type: 'string',
                description:
                  'Optional. Only valid when tab is "deviation". Sets the attribute filter at the same time as navigating. Use this when navigating to the Deviation tab after an analysis so the correct attribute is shown without a second tool call.',
              },
            },
            required: ['tab'],
          },
          handler: async ({ tab, attributeId }) => {
            if (!clusteringStore.selectedScheme) {
              return '[Tool] Action failed: no cluster scheme is selected. Use select-cluster-scheme first.';
            }
            const tabDef = instance.proxy.tabs.find(tabEntry => tabEntry.componentName === tab);
            if (!tabDef) {
              return `[Tool] Action failed: tab "${tab}" not found. Valid tabs: comparison, deviation, store-allocation.`;
            }
            instance.proxy.goToTab(tabDef);
            if (tab === 'deviation' && attributeId) {
              const attrs = clusteringStore.getClusterSchemeAttributes;
              // Support both exact ID match and case-insensitive name match
              const attr =
                attrs.find(a => a.id === attributeId) ||
                attrs.find(a => a.name.toLowerCase() === attributeId.toLowerCase());
              if (attr) {
                await clusteringStore.changeSelectedAttribute(attr.id);
                return `[Tool] Action succeeded: navigated to the deviation tab and set attribute filter to "${attr.name}".`;
              }
              return `[Tool] Action succeeded: navigated to the deviation tab, but attribute "${attributeId}" was not found. Available: ${attrs.map(a => `${a.name} (id: ${a.id})`).join(', ')}.`;
            }
            return `[Tool] Action succeeded: navigated to the ${tab} tab.`;
          },
        },
        'change-attribute-filter': {
          description:
            'Change the attribute displayed in the Deviation tab. Only applicable when the deviation tab is active.',
          workingLabel: 'Changing attribute filter',
          readOnly: true,
          parameters: {
            type: 'object',
            properties: {
              attributeId: {
                type: 'string',
                description: 'The ID of the attribute to display on the deviation tab.',
              },
            },
            required: ['attributeId'],
          },
          handler: async ({ attributeId }) => {
            const attributes = clusteringStore.getClusterSchemeAttributes;
            const attr = attributes.find(a => a.id === attributeId);
            if (!attr) {
              const available = attributes.map(a => `${a.name} (id: ${a.id})`).join(', ');
              return `[Tool] Action failed: attribute "${attributeId}" not found. Available: ${available || 'none'}.`;
            }
            await clusteringStore.changeSelectedAttribute(attributeId);
            return `[Tool] Action succeeded: changed attribute filter to "${attr.name}".`;
          },
        },
        'run-clustered-switching': {
          description:
            'Run clustered switching for the currently selected scheme. Triggers an analytics job that applies the same price/margin calculation method to all clusters. Requires a selected scheme and no job currently in progress.',
          workingLabel: 'Starting clustered switching',
          confirmationLabel: args =>
            `run clustered switching with ${args.priceMarginsOption} price/margin option for scheme "${clusteringStore.selectedScheme?.name}"`,
          parameters: {
            type: 'object',
            properties: {
              priceMarginsOption: {
                type: 'string',
                enum: ['hybrid', 'static', 'calculated'],
                description:
                  'Price and margin calculation method: "hybrid" (user-edited values for amended products, calculated for others), "static" (same unclustered values for all clusters), "calculated" (different clustered values per cluster).',
              },
            },
            required: ['priceMarginsOption'],
          },
          handler: async ({ priceMarginsOption }) => {
            const scheme = clusteringStore.selectedScheme;
            if (!scheme) {
              return '[Tool] Action failed: no cluster scheme is selected. Use select-cluster-scheme first.';
            }
            if (instance.proxy.hasJobInProgress) {
              return '[Tool] Action failed: a job is already in progress. Wait for it to complete before running clustered switching.';
            }
            if (instance.proxy.isEditingDisabled) {
              return '[Tool] Action failed: editing is disabled. Inform the user they do not have permission to run clustered switching.';
            }
            const validMethods = ['hybrid', 'static', 'calculated'];
            if (!validMethods.includes(priceMarginsOption)) {
              return `[Tool] Action failed: invalid priceMarginsOption "${priceMarginsOption}". Valid values: ${validMethods.join(', ')}.`;
            }
            const clusterConfiguration = (scheme.clusters || []).map(c => ({
              clusterId: c.clusterId,
              calculationMethod: priceMarginsOption,
              usesUnclusteredMatrix: false,
            }));
            const payload = {
              clusterSchemes: [scheme._id],
              clusterConfiguration,
            };
            await instance.proxy.processClusteredSwitching(payload);
            return `[Tool] Action succeeded: clustered switching job started for scheme "${scheme.name}" with ${priceMarginsOption} price/margin option.`;
          },
        },
        'analyse-clustering-deviations': {
          description:
            'Aggregates per-attribute deviation data for the selected cluster scheme and returns an AI analysis. ' +
            'ALWAYS call this for any question about deviation data, cluster differences, attribute importance, or what the chart shows — never answer with specific numbers from memory. ' +
            'Call this whenever the user is on the Deviation tab and asks about the data, the graph, what the chart shows, ' +
            'how clusters compare, or wants help understanding the attribute — treat any data question on the Deviation tab as a request for analysis. ' +
            'Also call this when the user asks about a specific attribute from another tab. ' +
            'Pass navigateToAttributeId when the user asks to both analyse AND navigate in one request (analysis + navigation as a single step). ' +
            'When navigating after already delivering an analysis (e.g. user says "yes" to a navigation offer), use navigate-to-tab + change-attribute-filter instead. ' +
            'Only disambiguate (ask whether they want to navigate vs analyse) when the user is on a DIFFERENT tab and mentions "deviations" without naming a specific attribute or asking a data question.',
          workingLabel: 'Analysing deviations',
          readOnly: true,
          parameters: {
            type: 'object',
            properties: {
              navigateToAttributeId: {
                type: 'string',
                description:
                  'Optional. Use ONLY when the user explicitly asks to analyse AND navigate to the Deviation tab in a single combined request. ' +
                  'Do NOT use this after navigate-to-tab has already been called — use change-attribute-filter instead. ' +
                  'Do NOT use this when the user just confirmed a navigation offer after receiving an analysis.',
              },
            },
            required: [],
          },
          handler: async ({ navigateToAttributeId } = {}) => {
            const scheme = clusteringStore.selectedScheme;
            if (!scheme) {
              return '[Tool] No cluster scheme is currently selected — please select one first.';
            }
            const rawDeviations = clusteringStore.clusterSchemeInformation?.deviations;
            if (!rawDeviations?.length) {
              return '[Tool] Deviation data is not yet loaded. Try selecting the scheme again.';
            }
            // Build attribute name lookup
            const allAttributes = scenariosStore.getCustomerFacingAndReportingAttributes || [];
            const attributesById = keyBy(allAttributes, 'id');
            // Aggregate per-store deviation rows into per-cluster averages
            const aggregated = rawDeviations.map(allAttributeDeviations => {
              const deviationsByAttributeValue = groupBy(
                allAttributeDeviations.attributeStoreDeviations,
                'attributeValue'
              );
              const summaryByAttributeValue = map(
                deviationsByAttributeValue,
                (attributeDeviations, attributeValue) => {
                  const totalAvg =
                    sumBy(attributeDeviations, 'deviation') / attributeDeviations.length;
                  const deviationsByCluster = scheme.clusters.map(cluster => {
                    const clusterDeviations = attributeDeviations
                      .filter(row => cluster.storeKeys.includes(row.storeKey))
                      .map(row => row.deviation);
                    if (!clusterDeviations.length) return [];
                    const clusterAvg =
                      clusterDeviations.reduce((a, b) => a + b, 0) / clusterDeviations.length;
                    return {
                      cluster: cluster.clusterName,
                      deviation: Math.round((clusterAvg - totalAvg) * 1e6) / 1e6,
                    };
                  });
                  return { value: attributeValue, deviationsByCluster };
                }
              );
              return {
                attribute: attributesById[allAttributeDeviations.attributeId]?.name,
                deviations: summaryByAttributeValue,
              };
            });
            const currentTab = instance.proxy.selectedTab?.componentName;
            const selectedAttrId = clusteringStore.selectedAttributeId;
            const deviationAttribute = selectedAttrId
              ? attributesById[selectedAttrId]?.name || null
              : null;
            const attributeNote = deviationAttribute
              ? `The user is currently viewing the "${deviationAttribute}" attribute on the Deviation tab. Lead with insights for this attribute, then highlight any other notable deviations.\n\n`
              : '';
            const navigationHint =
              currentTab !== 'deviation' && !navigateToAttributeId
                ? '\n\nPOST-ANALYSIS NOTE: The user is not on the Deviation tab. After delivering your analysis, end with a brief offer to navigate there AND set the attribute filter to the one you just analysed — e.g. "Would you like me to take you to the Deviation tab and filter to [attribute name] so you can see the graph?". If the user asked about a specific attribute, name it explicitly in the offer.'
                : '';
            let result =
              DEVIATIONS_GUIDANCE + attributeNote + JSON.stringify(aggregated) + navigationHint;
            if (navigateToAttributeId) {
              const deviationTabDef = instance.proxy.tabs?.find(
                tabEntry => tabEntry.componentName === 'deviation'
              );
              if (deviationTabDef) instance.proxy.goToTab(deviationTabDef);
              await clusteringStore.changeSelectedAttribute(navigateToAttributeId);
              const attr = attributesById[navigateToAttributeId];
              result += `\n\n[Navigation complete: now on Deviation tab, attribute filter set to "${attr?.name || navigateToAttributeId}".]`;
            }
            return result;
          },
        },
      },
    });

    return {
      // permissions
      canEditClusteringPage,
      isUnsavedDataModalOpen,
      // unsaved data warning
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      beforeNavWithUnsavedData,
      // i18n
      tkey,
      t,
      // formatters
      formatNumber,
      formatScenarioProgress,
      // features
      aiPanelEnabled,
      aiClusteringAnalysisEnabled,
      productSwitchingServiceEnabled,
      startClusteredSwitchingPoller,
      switchingClusters,
      clusteredSwitchingProgress,
    };
  },

  data() {
    return {
      CELL_TYPE: TableEnum.CELL_TYPE,
      comparisonSchemaId: null,
      selectedClusterToMoveTo: null, // used for store-allocation-list edit
      hasDataChanges: false, // only used for unsaved-data warning. component uses v-if and :is, can't rely on refs
      hasSelectedStores: false, // used to disable cluster select when no stores selected
      listMapToggleData: 0, // 0 for list, 1 for map
      isClusterGenerationModalOpen: false,
      isClusteredSwitchingModalOpen: false,
      headers: [
        {
          value: 'name',
          ui: TableEnum.CELL_TYPE.readonly,
          id: 'schemeName',
          cols: 5,
          headerClass: 'pl-2 px-0 py-0 pr-0',
          colClass: 'pl-2 px-0 py-0 pr-0',
          onClick: this.handleExpandClusteringScheme,
        },
        {
          text: this.tkey('clusterCount'),
          value: 'clusterCount',
          class: 'pl-0',
          cols: 3,
          headerClass: 'pa-0 header-text',
          colClass: 'pa-0 justify-end',
          ui: TableEnum.CELL_TYPE.readonly,
          id: 'clusterCount',
        },
        {
          text: this.tkey('varianceExplained'),
          value: 'variance',
          class: 'px-0',
          headerClass: 'pa-0 header-text',
          colClass: 'pr-0 justify-end',
          cols: 3,
          ui: TableEnum.CELL_TYPE.readonly,
          type: 'percentage',
          id: 'clusterVariance',
        },
        {
          class: 'pa-0',
          cols: 1,
          ui: TableEnum.CELL_TYPE.icon,
          colClass: 'justify-end px-0 py-0',
          onClick: this.handleExpandClusteringScheme,
          value: 'mdi-chevron-right',
          id: 'expandButton',
        },
      ],
      currentTab: null,
      previousTab: null,
      clickedTab: null,
    };
  },

  computed: {
    ...mapState(useClusteringStore, [
      'isLoadingClusterInformation',
      'selectedAttributeId',
      'selectedScheme',
      'comparisonSchemeId',
      'selectableClusterNames',
      'isLoadingClusterSchemes',
      'getScenarioClusterSchemes',
      'getClusterSchemeAttributes',
      'getScenarioClusterSchemesForExport',
      'getScenarioClusterSchemeById',
    ]),
    ...mapState(useContextStore, [
      'clientConfig',
      'allowTabChange',
      'showNotImplemented',
      'getClientConfig',
    ]),

    ...mapState(useScenariosStore, ['isJobRunning', 'selectedScenario', 'jobStatuses']),
    ...mapState(useAiStore, ['isSidePanelOpen']),

    storeAllocationTab() {
      return {
        routeName: 'store-allocation',
        componentName: 'store-allocation',
        title: this.tkey('tabs.storeAllocation.title'),
        position: 2,
      };
    },

    tabs() {
      return [
        {
          routeName: 'comparison',
          componentName: 'comparison',
          title: this.tkey('tabs.comparison.title'),
          position: 0,
        },
        {
          routeName: 'deviation',
          componentName: 'deviation',
          title: this.tkey('tabs.deviation.title'),
          position: 1,
        },
        this.storeAllocationTab,
      ];
    },

    hasDefaultClustersEnabled() {
      return get(this.getClientConfig, 'features.defaultClustersEnabled', false);
    },

    listMapToggle: {
      get() {
        return this.allowTabChange ? this.listMapToggleData : 0;
      },
      set(newValue) {
        this.beforeNavWithUnsavedData(() => {
          this.listMapToggleData = newValue;
          this.goToTab(this.storeAllocationTab);
        });
      },
    },

    selectedTab() {
      return this.tabs.find(tab => tab && this.$route.path.includes(tab.routeName)) || this.tabs[0];
    },

    getInfoTextForTab() {
      if (this.isStoreAllocationMapView) return this.tkey('tabs.storeAllocation.infoTextMapView');
      return this.tkey(`tabs.${camelCase(this.selectedTab.componentName)}.infoText`);
    },

    selectedTabPosition() {
      return this.selectedTab ? this.selectedTab.position : 0;
    },

    comparisonSchemas() {
      const schemas = [
        {
          _id: null,
          name: this.tkey('emptySelection'),
        },
      ];
      this.groupedSchemes.forEach(group => {
        group.schemes.forEach(schema => {
          if (has(schema, '_id') && this.selectedScheme._id !== schema._id) {
            schemas.push(schema);
          }
        });
      });
      return schemas;
    },

    componentName() {
      return this.getComponentName(this.selectedTab.componentName);
    },

    // disable switching button
    hasDataErrors() {
      return this.hasJobInProgress || this.getScenarioClusterSchemes.length < 1;
    },

    isNavigationDisabled() {
      return (
        !this.getScenarioClusterSchemes.length ||
        this.hasJobInProgress ||
        this.isLoadingClusterSchemes
      );
    },

    isRunningClusteringJob() {
      return this.isJobRunning('clusteringGenerator');
    },

    userCanSeeSchemeInformation() {
      return (
        !this.isRunningClusteringJob && this.selectedScheme && !this.isLoadingClusterInformation
      );
    },

    showAttributeFilter() {
      return ['deviation'].includes(this.selectedTab.componentName);
    },

    showCompareWithFilter() {
      return ['comparison'].includes(this.selectedTab.componentName);
    },

    showListModeToggle() {
      return this.selectedTab.componentName === 'store-allocation';
    },

    groupedSchemes() {
      const [inScope, outOfScope] = partition(this.getScenarioClusterSchemes, 'selected');
      if (!inScope.length) {
        // return ungrouped
        return [{ schemes: outOfScope }];
      }
      // just to inform the user unclustered is also in scope
      inScope.unshift({
        variance: 0,
        clusterCount: 1,
        name: this.tkey('unclustered'),
        disabled: true,
      });

      return [
        { text: this.tkey('inScope'), schemes: inScope, inScope: true },
        { text: this.tkey('outOfScope'), schemes: outOfScope, inScope: false },
      ];
    },

    isRunningClusteredSwitchingJob() {
      return this.isJobRunning('clusteredSwitching');
    },

    userCanRunClusteredSwitching() {
      const hasJobInProgress = this.isRunningClusteredSwitchingJob || this.isRunningClusteringJob;

      return !hasJobInProgress || !!this.getScenarioClusterSchemes.length;
    },

    hasJobInProgress() {
      return this.isRunningClusteredSwitchingJob || this.isRunningClusteringJob;
    },

    getProgressPercent() {
      if (this.isRunningClusteredSwitchingJob) {
        return this.productSwitchingServiceEnabled
          ? this.clusteredSwitchingProgress
          : this.jobStatuses.clusteredSwitching.progress;
      }
      if (this.isRunningClusteringJob) return this.jobStatuses.clusteringGenerator.progress;
      return null;
    },

    getProgressStatus() {
      if (this.isRunningClusteredSwitchingJob) return this.jobStatuses.clusteredSwitching.status;
      if (this.isRunningClusteringJob) return this.jobStatuses.clusteringGenerator.status;
      return null;
    },

    isStoreAllocationMapView() {
      return this.selectedTab.componentName === 'store-allocation' && this.listMapToggle === 1;
    },

    isStoreAllocationListView() {
      return this.selectedTab.componentName === 'store-allocation' && this.listMapToggle === 0;
    },

    showClusterMapToggle() {
      return get(this.clientConfig, 'features.clusterMapEnabled');
    },

    isEditingDisabled() {
      return !this.canEditClusteringPage;
    },

    invalidClusteringSchemes() {
      return filter(this.getScenarioClusterSchemes, clScheme => {
        // cluster is invalid if it's marked as no data cluster
        return some(clScheme.clusters, 'isNoDataCluster');
      });
    },
  },

  watch: {
    selectedScheme() {
      // keep url up to date, don't need handleTabSelect since url change triggers beforeRouteLeave
      this.goToTab(this.selectedTab);
      if (!this.selectedScheme) return;
      this.setSelectableClusterNames(this.selectedScheme.clusters.map(c => c.clusterName));
    },
  },

  mounted() {
    // Set up AI side panel (outer shell only; inner context is built lazily at send-time)
    const aiStore = useAiStore();
    const contextDisplay = [this.selectedScenario?.name, this.selectedScheme?.name].filter(Boolean);
    const insightPrompts = this.aiClusteringAnalysisEnabled
      ? [
          {
            label: 'Attribute deviations',
            prompt:
              this.t('aiPrompts.clustering.clusterSchemeDeviations') ||
              'Identify any significant attribute deviations in the current clustering scheme',
          },
        ]
      : [];
    // Pass 'clustering' as the anchor so the route watcher does not clear context
    // when the user navigates between sub-tabs (comparison, deviation, store-allocation).
    aiStore.enableSidePanel(
      { section: 'clustering', contextDisplay, insightPrompts },
      'clustering'
    );
    aiStore.setSidePanelContextFactory(() => this.buildInnerContext());
  },

  async created() {
    // listMapToggle is 0 on created, but if url ends with map, we need to select it
    // This needs to happen as soon as the component is created to prevent race conditions from
    // switching back to the list view
    if (this.$route.path.endsWith('store-allocation-map')) {
      this.listMapToggleData = 1;
    }

    await this.fetchScenarioClusters();
    await this.fetchSwitchingMatricesCount();
    if (this.hasDefaultClustersEnabled) {
      await this.fetchClusterSchemes();
      await this.getStores();
      await this.setValidClusterSchemes();
    }
    const routeScheme = this.getScenarioClusterSchemeById(this.$route.params.clusterSchemeId);
    if (routeScheme) {
      this.expandClusteringScheme(routeScheme);
    }

    this.setSelectableClusterNames(
      this.selectedScheme ? this.selectedScheme.clusters.map(c => c.clusterName) : []
    );

    this.currentTab = this.selectedTabPosition;
    this.previousTab = this.currentTab;

    const vm = this;
    this.emitter.on('unsaved-data-confirm', () => {
      vm.onTabChange(vm.clickedTab);
    });
    if (this.selectedScheme && this.selectedScheme.scenarioId !== this.selectedScenario._id) {
      // Deselect the previously selected scheme if the user has changed scenario
      this.setSelectedScheme(null);
    }
    // Signal that page data is ready for the agent to act on.
    useAiStore().setContextReady(true);
  },

  methods: {
    completedCannGroupCount(cluster) {
      return (cluster.cannGroups ?? []).filter(cg =>
        switchingJobStatuses.terminalSwitchingJobStatuses.has(cg.status)
      ).length;
    },

    clusterStatusIcon(status) {
      if (status === switchingJobStatuses.completed) return 'mdi-check-circle';
      if (switchingJobStatuses.failureSwitchingJobStatuses.has(status)) return 'mdi-close-circle';
      if (status === switchingJobStatuses.inProgress) return 'mdi-loading';
      return 'mdi-clock-outline';
    },

    clusterStatusIconColor(status) {
      if (status === switchingJobStatuses.completed) return 'success';
      if (switchingJobStatuses.failureSwitchingJobStatuses.has(status)) return 'error';
      if (status === switchingJobStatuses.inProgress) return 'primary';
      return 'grey';
    },

    ...mapActions(useClusteringStore, [
      'fetchScenarioClusters',
      'saveClusteringSchemes',
      'getClusterSchemeData',
      'changeSelectedAttribute',
      'runClusteredSwitching',
      'setSelectedScheme',
      'updateSelectedAttributeId',
      'setComparisonSchemeId',
      'setSelectableClusterNames',
    ]),
    ...mapActions(useScenariosStore, [
      'refreshScenario',
      'updateScenario',
      'setScenarioHasSkippedClustering',
    ]),
    ...mapActions(useSnackbarStore, ['showSuccess', 'showError']),
    ...mapActions(useSwitchingMatricesStore, ['fetchSwitchingMatricesCount']),
    ...mapActions(useReferenceClusteringStore, ['fetchClusterSchemes', 'setValidClusterSchemes']),
    ...mapActions(useToolDataStore, ['getStores']),

    moveStores(groupClusterName) {
      this.$refs['store-allocation-list'].moveRowsToGroup({ groupClusterName });
      // without $nextTick first reset will work but all subsequent ones won't work.
      // The vuetify internal lazy value gets out of sync with the actual value.
      // similar issue on promo page-navigator.vue cancelUpdatePageCount.
      this.$nextTick(() => {
        this.selectedClusterToMoveTo = null;
      });
    },

    getComponentName(componentName) {
      if (componentName === 'store-allocation') {
        const layout = this.listMapToggle ? 'map' : 'list';
        return `store-allocation-${layout}`;
      }

      return componentName;
    },

    async onTabChange(clickedTab) {
      this.clickedTab = clickedTab;
      this.currentTab = clickedTab;
      await this.$nextTick();
      if (!this.allowTabChange) this.currentTab = this.previousTab;
      else this.previousTab = this.currentTab;
    },

    goToTab(tab) {
      this.currentTab = tab.position;
      if (!this.selectedScheme) return;
      const route = this.generateTabRoute(tab);
      // Prevent unnecessary updates in history
      this.$router.replace(route);
    },

    generateTabRoute(tab) {
      if (!this.selectedScheme) return {};
      return {
        name: this.getComponentName(tab.componentName),
        params: {
          clusterSchemeId: this.selectedScheme._id,
          scenarioId: this.selectedScenario._id,
        },
        query: this.$route.query,
      };
    },

    handleTabSelect(tab) {
      this.beforeNavWithUnsavedData(() => {
        this.currentTab = tab.position;
      });
    },

    setSelectedComparisonScheme(schemeId) {
      this.setComparisonSchemeId(
        schemeId === this.selectedScheme._id || !schemeId ? null : schemeId
      );
    },
    closeClusterGenerationModal() {
      this.isClusterGenerationModalOpen = false;
    },
    closeClusteredSwitchingModal() {
      this.isClusteredSwitchingModalOpen = false;
    },
    openClusterGenerationModal() {
      this.isClusterGenerationModalOpen = true;
    },
    openClusterSwitchingModal() {
      this.isClusteredSwitchingModalOpen = true;
    },

    expandClusteringScheme(clusterScheme) {
      if (clusterScheme.disabled) return;
      this.getClusterSchemeData(clusterScheme);
      this.setSelectedScheme(clusterScheme);
      this.updateSelectedAttributeId();
      this.comparisonSchemaId = null;
    },

    handleExpandClusteringScheme(clusterScheme, tab) {
      this.beforeNavWithUnsavedData(() => {
        this.expandClusteringScheme(clusterScheme);
        if (tab) {
          this.goToTab(tab);
        }
      });
    },

    async processNewClusterSchemes(body) {
      await this.saveClusteringSchemes(body);
      await this.refreshScenario();
    },

    getClusterVariance(headerType, value) {
      if (headerType === 'percentage') {
        return value ? `${this.formatNumber({ number: value, format: 'percent' })}%` : 0;
      }
      return value;
    },

    showClusterWarning(headerValue, scheme) {
      return headerValue === 'name' && includes(this.invalidClusteringSchemes, scheme);
    },

    async processClusteredSwitching(payload) {
      await this.runClusteredSwitching(payload);
      await this.refreshScenario();
      if (this.productSwitchingServiceEnabled) this.startClusteredSwitchingPoller();
    },

    // Build inner context object for the AI assistant (called lazily at send-time)
    buildInnerContext() {
      const selectedAttr = this.selectedAttributeId
        ? this.getClusterSchemeAttributes?.find(a => a.id === this.selectedAttributeId)
        : null;
      return {
        scenarioId: this.selectedScenario?._id,
        clusterSchemeId: this.selectedScheme?._id,
        scenarioName: this.selectedScenario?.name || 'No scenario selected',
        currentView: this.selectedTab?.componentName || 'comparison',
        deviationAttribute: selectedAttr?.name || null,
        scenarioProgress: this.formatScenarioProgress,
        schemesCount: this.getScenarioClusterSchemes?.length || 0,
        hasJobInProgress: this.hasJobInProgress,
        invalidSchemesCount: this.invalidClusteringSchemes?.length || 0,
        selectedScheme: this.selectedScheme
          ? {
              name: this.selectedScheme.name,
              clusterCount: this.selectedScheme.clusters?.length || 0,
              varianceExplained: this.selectedScheme.variance,
              source: this.selectedScheme.source,
              clusters:
                this.selectedScheme.clusters?.map(c => ({
                  name: c.clusterName,
                  storeCount: c.storeKeys?.length || 0,
                  isNoDataCluster: c.isNoDataCluster || false,
                })) || [],
            }
          : null,
        allSchemes:
          this.getScenarioClusterSchemes?.map(scheme => ({
            name: scheme.name,
            clusterCount: scheme.clusters?.length || 0,
            varianceExplained: scheme.variance,
            isSelected: scheme._id === this.selectedScheme?._id,
          })) || [],
        features: {},
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

$shadow-colour1: rgba(0, 0, 0, 0.2);
$shadow-colour2: rgba(0, 0, 0, 0.14);
$shadow-colour3: rgba(0, 0, 0, 0.12);

.page-actions-container {
  border-top: 1px solid $assortment-panel-border-divider-colour;
}

.first-tab {
  margin-left: 0 !important;
  border-left: 0 !important;
}

.created-cluster-schemes {
  overflow: auto;
  padding: 0;
  height: 100%;
  border-top-width: 1px;
}

#clustering-tab-content {
  overflow: hidden;
  max-height: 100%;
  display: flex;
  flex-direction: column;
}

#cluster-schemes-table {
  padding: 0;

  .cluster-row {
    &:nth-last-of-type(odd) {
      background-color: $assortment-table-blue-bg-colour;
    }

    &.selected {
      position: relative;
      /* override existing border with this new one */
      border-left: 2px solid $assortment-action-icon-color;
      box-shadow:
        0px 3px 1px -2px $shadow-colour1,
        0px 2px 2px 0px $shadow-colour2,
        0px 1px 5px 0px $shadow-colour3;

      &::after {
        content: '';
        position: absolute;
        width: 100%;
        top: 0;
        pointer-events: none;
        bottom: 0;
      }
    }
    &.in-scope:last-child {
      margin-bottom: 15px;
    }
  }
}

.v-icon-blue {
  color: $assortment-action-icon-color !important;
  fill: $assortment-action-icon-color !important; // for SVG
}

span {
  color: $assortment-text-colour;
}

.empty-table {
  color: $assortment-disabled-action-green-button-colour;
}

#main-row {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.step-tab {
  font-size: 13px;
  background-color: $assortment-unselected-tab-colour;
  color: $assortment-inactive-tab-text-colour;
  border: 0.3px solid $assortment-border-colour;
  border-left: 3px solid $assortment-border-colour;
  justify-content: flex-start;
  align-items: center;
  padding: 0 1rem;
  min-width: 20rem !important;
  font-weight: bold;
  line-height: 1;
  display: flex;

  &:not(:first-child) {
    margin-left: 0.4rem;
  }
}

.v-tab--selected {
  background-color: $assortment-selected-tab-colour;
  color: $assortment-text-colour;
  border-bottom: 1px solid $assortment-selected-tab-colour;
  position: relative;
  z-index: 1;
  margin-bottom: -1px;
}

:deep(.v-slide-group__content) {
  border-bottom: 1px solid $assortment-border-colour;
}

:deep(.v-tabs) {
  --v-tabs-height: 37px;
}

:deep(.v-tabs-bar) {
  height: 37px;
  --v-tabs-height: 37px;
}

:deep(.v-tabs.theme--light > .v-tabs-bar) {
  background-color: inherit !important;
}

:deep(.header-text .disabled > span) {
  color: $assortment-disabled-action-green-button-colour;
}

:deep(.v-input__slot) {
  height: 25px;
  border-bottom: 1px solid $assortment-primary-colour;
}

:deep(.v-select) {
  height: 32px;
}

:deep(.v-select .v-field) {
  height: 32px;
}

:deep(.v-select .v-field__input) {
  min-height: 32px;
  padding-top: 4px;
  padding-bottom: 4px;
}

:deep(#vselectClusterName) {
  // ensure entire help text is displayed
  min-width: 160px;
}

#headers-row {
  min-height: 32px;
  max-height: 48px;
  overflow: hidden;
}

.header-text {
  text-align: right;
  padding-top: 0px;
  padding-bottom: 0px;
  min-width: 50px;
}

.header-text > span {
  font-size: 10px;
  line-height: 1.2;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.in-progress {
  opacity: 0.3;
}
.center-top {
  position: absolute;
  left: 50%;
  bottom: 50%;

  & > .progress-bar {
    position: relative;
    left: -50%;
    width: 500px;
  }

  & > .switching-cluster-status {
    position: relative;
    left: -50%;
    width: 500px;
    margin-top: 1rem;
    font-size: 13px;

    &__scheme-name {
      font-weight: 600;
      margin-bottom: 0.5rem;
      text-align: center;
    }

    &__row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 2px 0;
    }

    &__cluster-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__cann-group-count {
      color: $assortment-text-colour;
      white-space: nowrap;
    }
  }
}

.cluster-warning-container {
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.error-triangle-icon {
  color: $assortment-negative-action-colour;
  font-size: 2rem !important;
  cursor: pointer;
}
</style>
